import { Component, effect, viewChild, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { ServiceSettingsDoctorRecordStore } from './service-settings-doctor-record.store';
import { SubscriptionComponent } from '../../../../../shared/helpers/subscription.component';
import { ConfirmBuilder } from '../../../../../shared/service/confirm.builder';
import {
    ServiceDoctorRecordTypeObject,
} from '@libs/types/src/services/object-type/service/settings/service-docker-record-type.object';
import {
    ServiceDoctorRecordTypeModalComponent,
} from '../../../../components/services/settings/service-doctor-record-type-modal/service-doctor-record-type-modal.component';
import { activeColumn, descriptionColumn, nameColumn } from '../../../../../shared/components/kit/table/table.fields';
import { TableColumnType } from '../../../../../shared/components/kit/table/table.types';
import { TableComponent } from '../../../../../shared/components/kit/table/table.component';
import { contentGlobalSuccess } from '../../../../content.error';
import {
    ServiceSettingsCommonComponent,
} from '../../../../components/services/settings/service-settings-common/service-settings-common.component';
import { fb, isAllowed, routeParams } from '../../../../../shared/shared.global-service';
import {
    SERVICE_SETTINGS_DOCTOR_RECORD_TYPE_KEY,
} from '@libs/types/src/services/types/service-settings-doctor-record.type';
import { ActivatedRoute, Router } from '@angular/router';
import { Divider } from '@openng/optimus-ui/divider';
import { CheckInputComponent } from '../../../../../../components/kit/input/check-input/check-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { ActionButtonsComponent } from '../../../../../../components/kit/action-buttons/action-buttons.component';
import { CardTableComponent } from '../../../../../shared/components/kit/table/card-table.component';
import {
    transformSingleServiceProcedure,
} from '../../../../../shared/components/kit/performed-procedures/performed-procedures.pipes';

@Component({
    selector: 'app-service-settings-doctor-record',
    templateUrl: 'service-settings-doctor-record.component.html',
    styleUrl: 'service-settings-doctor-record.component.scss',
    providers: [
        ServiceSettingsDoctorRecordStore,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ServiceSettingsCommonComponent,
        Divider,
        CheckInputComponent,
        TranslatePipe,
        ActionButtonsComponent,
        CardTableComponent,
        ServiceDoctorRecordTypeModalComponent,
    ],
})
export class ServiceSettingsDoctorRecordComponent extends SubscriptionComponent {

    protected readonly store = inject(ServiceSettingsDoctorRecordStore);
    private readonly confirm = inject(ConfirmBuilder);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);

    protected doctorTypeModal = viewChild(ServiceDoctorRecordTypeModalComponent);
    @ViewChild('doctorRecordTypeTable') doctorRecordTypeTable!: TableComponent;
    @ViewChild(ServiceSettingsCommonComponent) serviceSettingsCommonComponent!: ServiceSettingsCommonComponent;

    protected form = fb.group({
        showNurseRecord: fb.control<boolean>(true),
        useVisits: fb.control<boolean>(true),
        useInterventions: fb.control<boolean>(true),
        showRecordTime: fb.control<boolean>(true),
        showMainDiagnosis: fb.control<boolean>(true),
        showReferringDoctor: fb.control<boolean>(true),
        showEpicrisis: fb.control<boolean>(true),
    });

    protected serviceId = 0;

    protected doctorRecordTypeColumns = [
        activeColumn,
        nameColumn,
        {
            name: 'procedures',
            type: TableColumnType.PerformedProcedures,
            filterable: true,
            data: (items: ServiceDoctorRecordTypeObject['procedures']) =>
                items?.map((item) => transformSingleServiceProcedure(item)) ?? [],
        },
        descriptionColumn,
    ];

    constructor() {
        super();
        this.store.onSuccess(
            () => this.doctorRecordTypeTable?.reload(),
            'createServiceDoctorRecordType',
            'updateServiceDoctorRecordType',
            'deleteServiceDoctorRecordType',
        );

        effect(() => {
            this.serviceId = this.store.service()?.id || 0;
        });

        effect(() => {
            const defaultValues = this.store.doctorRecordSettings();
            if (defaultValues) {
                this.form.patchValue({
                    showNurseRecord: defaultValues?.showNurseRecord ?? true,
                    useVisits: defaultValues?.useVisits ?? true,
                    useInterventions: defaultValues?.useInterventions ?? true,
                    showRecordTime: defaultValues?.showRecordTime ?? true,
                    showMainDiagnosis: defaultValues?.showMainDiagnosis ?? true,
                    showReferringDoctor: defaultValues?.showReferringDoctor ?? true,
                    showEpicrisis: defaultValues?.showEpicrisis ?? true,
                });
            }
        });
    }

    save() {
        const serviceId = routeParams.serviceId();
        if (!serviceId) return;

        const data = {
            ...JSON.parse(this.serviceSettingsCommonComponent.getFormData()),
            ...this.form.getRawValue(),
        };

        this.store.api.updateServiceSettings({
            key: SERVICE_SETTINGS_DOCTOR_RECORD_TYPE_KEY,
            data: JSON.stringify(data),
            serviceId: Number(serviceId),
        }).then(() => {
            contentGlobalSuccess.next('common.updated');
        });
    }

    async cancel() {
        await this.router.navigate(['../../basic-information'], { relativeTo: this.route });
    }

    protected createDoctorType(): void {
        this.doctorTypeModal()?.show();
    }

    protected updateDoctorType(item: ServiceDoctorRecordTypeObject): void {
        this.doctorTypeModal()?.show(true, item);
    }

    protected deleteDoctorType(item: any): void {
        this.confirm.confirmDelete(
            () => this.store.api.deleteServiceDoctorRecordType({ id: item.item.id })
                .then(() => contentGlobalSuccess.next('common.deleted')),
            item,
        );
    }

    protected readonly isAllowed = isAllowed;
}
